import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, Users, Mail, User, LayoutGrid, Shield, Key, Eye, EyeOff, Upload, X, Phone } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton, CustomSelect, CustomSearchableSelect } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { employeeApi } from '../api/employeeApi';
import { departmentApi } from '../api/departmentApi';
import { Department, DepartmentLevel } from '../types';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';

const addEmployeeSchema = z.object({
    email: z.string().email('Invalid email address'),
    name: z.string().min(2, 'Name must be at least 2 characters'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
    department_ids: z.array(z.string()).min(1, 'At least one department is required'),
    phone_number: z.string()
        .min(1, 'Phone number is required')
        .regex(/^[+]?[0-9]{10,15}$/, 'Phone number must be between 10 and 15 digits'),
    designation: z.string().min(1, 'Designation is required'),
});

type AddEmployeeFormData = z.infer<typeof addEmployeeSchema>;

export const AddEmployee = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [photo, setPhoto] = useState<File | null>(null);
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const { showToast } = useToastStore();

    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        formState: { errors },
    } = useForm<AddEmployeeFormData>({
        resolver: zodResolver(addEmployeeSchema),
        defaultValues: {
            department_ids: [],
            phone_number: '',
            designation: ''
        }
    });



    useEffect(() => {
        if (schoolSlug) {
            fetchDepartments();
        }
    }, [schoolSlug]);

    const fetchDepartments = async () => {
        const response = await departmentApi.getDepartments(schoolSlug, { page: 1, page_size: 50 });
        if (response.success && response.data) {
            const data = response.data as any;
            setDepartments(Array.isArray(data) ? data : (data.items || []));
        }
    };

    const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setPhoto(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPhotoPreview(reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    };

    const removePhoto = () => {
        setPhoto(null);
        setPhotoPreview(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const onSubmit = async (data: any) => {
        if (!schoolSlug) {
            showToast('School context not found', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const payload = { ...data, photo };
            const response = await employeeApi.inviteEmployee(schoolSlug, payload);
            if (response.success) {
                showToast('Employee added successfully!', 'success');
                navigate(`/${schoolSlug}/school-admin/employees`);
            } else {
                showToast(response.error || 'Failed to add employee', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Employees', path: `/${schoolSlug}/school-admin/employees`, icon: <Users size={14} /> },
                    { label: 'Add Employee' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Add New Employee"
                    subtitle="Please add details of employee"
                    icon={<Users size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        
                        {/* Photo Upload Section */}
                        <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>Profile Photo (Optional)</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                {photoPreview ? (
                                    <div style={{ position: 'relative', width: '80px', height: '80px', borderRadius: '50%', overflow: 'hidden', border: '2px solid var(--border-color)' }}>
                                        <img src={photoPreview} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        <button
                                            type="button"
                                            onClick={removePhoto}
                                            style={{
                                                position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
                                            }}
                                        >
                                            <X size={12} />
                                        </button>
                                    </div>
                                ) : (
                                    <div 
                                        style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed var(--border-color)', cursor: 'pointer', color: 'var(--text-muted)' }}
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <Upload size={24} />
                                    </div>
                                )}
                                <div>
                                    <CustomButton type="button" variant="outline" size="small" onClick={() => fileInputRef.current?.click()}>
                                        Upload Photo
                                    </CustomButton>
                                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>JPG, PNG, GIF max 5MB</p>
                                </div>
                                <input 
                                    type="file" 
                                    ref={fileInputRef} 
                                    onChange={handlePhotoChange} 
                                    accept="image/*" 
                                    style={{ display: 'none' }} 
                                />
                            </div>
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomSearchableSelect
                                label="Assign Departments"
                                isMulti
                                options={departments.map(d => ({ label: d.name, value: d.id }))}
                                value={watch('department_ids') || []}
                                onChange={(val) => setValue('department_ids', val, { shouldValidate: true })}
                                placeholder="Search and select departments..."
                                leftIcon={<LayoutGrid size={18} />}
                                error={errors.department_ids?.message}
                                hideAvatar
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Full Name"
                                placeholder="Enter employee name"
                                leftIcon={<User size={18} />}
                                error={errors.name?.message}
                                {...register('name')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Designation"
                                placeholder="e.g. Teacher, Coordinator"
                                leftIcon={<LayoutGrid size={18} />}
                                error={errors.designation?.message}
                                {...register('designation')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 1' }}>
                            <CustomInput
                                label="Phone Number"
                                placeholder="Enter phone number"
                                leftIcon={<Phone size={18} />}
                                error={errors.phone_number?.message}
                                {...register('phone_number')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Email Address"
                                type="email"
                                placeholder="employee@school.com"
                                leftIcon={<Mail size={18} />}
                                error={errors.email?.message}
                                {...register('email')}
                                required
                            />
                        </div>

                        <div style={{ gridColumn: 'span 2' }}>
                            <CustomInput
                                label="Initial Password"
                                type={showPassword ? 'text' : 'password'}
                                placeholder="Set initial password"
                                leftIcon={<Key size={18} />}
                                rightIcon={
                                    <div 
                                        onClick={() => setShowPassword(!showPassword)} 
                                        style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </div>
                                }
                                error={errors.password?.message}
                                {...register('password')}
                                required
                                helperText="Employee can change this password later."
                            />
                        </div>

                        <div style={{
                            gridColumn: 'span 2',
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Add Employee
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
